import React from 'react';
import {
  WhatsappShareButton,
  WhatsappIcon,
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TelegramShareButton,
  TelegramIcon,
  XIcon,
  EmailShareButton,
  EmailIcon
} from 'react-share';
import { Listing } from '../property/property-card/PropertyCardTypes';
import { ClockIcon } from 'lucide-react';

export const ShareToApplyButton: React.FC<{ listing: Listing }> = ({
  listing
}) => {
  const generateShareContent = () => ({
    title: `${listing.title} - Available in ${listing.location}`,
    text: `Found this ${listing.bedrooms}-bedroom ${listing.type} for €${listing.price} in ${listing.location} on RentSmart.ie! Check it out:`,
    url: `https://rentsmart.ie/property/${listing.id}`
  });

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 border-t border-white/20 p-2.5 sm:p-4 md:relative md:border-none md:rounded-2xl md:p-6 shadow-2xl z-50 backdrop-blur-sm">
      <div className="flex flex-col space-y-2 sm:space-y-3">
        {/* Header with animated icon */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md rounded-full px-3 py-1 sm:px-4 sm:py-2 mb-1 sm:mb-2">
            <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-1.5 sm:mr-2 animate-pulse" />
            <span className="text-white text-[10px] sm:text-xs font-semibold uppercase tracking-wide">
              Hot Property
            </span>
          </div>
          <h3 className="text-white text-sm sm:text-lg md:text-xl font-bold leading-tight px-2">
            Share with friends before it's gone!
          </h3>
          <p className="text-white/80 text-xs sm:text-sm mt-0.5 sm:mt-1 hidden sm:block">
            Help someone find their perfect home
          </p>
        </div>

        {/* Social Share Buttons */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 mt-1 sm:mt-3">
          <WhatsappShareButton url={generateShareContent().url}>
            <div className="flex flex-col items-center group transition-transform duration-300 hover:scale-110">
              <div className="rounded-full flex items-center justify-center bg-white/10 backdrop-blur-sm p-1.5 sm:p-2 group-hover:bg-white/20 transition-all duration-300 shadow-lg">
                <WhatsappIcon
                  size={30}
                  round
                  className="sm:w-[35px] sm:h-[35px]"
                />
              </div>
              <span className="text-white text-[10px] sm:text-xs font-medium mt-1 sm:mt-1.5 group-hover:text-yellow-300 transition-colors duration-300">
                WhatsApp
              </span>
            </div>
          </WhatsappShareButton>

          <FacebookShareButton url={generateShareContent().url}>
            <div className="flex flex-col items-center group transition-transform duration-300 hover:scale-110">
              <div className="rounded-full flex items-center justify-center bg-white/10 backdrop-blur-sm p-1.5 sm:p-2 group-hover:bg-white/20 transition-all duration-300 shadow-lg">
                <FacebookIcon
                  size={30}
                  round
                  className="sm:w-[35px] sm:h-[35px]"
                />
              </div>
              <span className="text-white text-[10px] sm:text-xs font-medium mt-1 sm:mt-1.5 group-hover:text-yellow-300 transition-colors duration-300">
                Facebook
              </span>
            </div>
          </FacebookShareButton>

          <TwitterShareButton url={generateShareContent().url}>
            <div className="flex flex-col items-center group transition-transform duration-300 hover:scale-110">
              <div className="rounded-full flex items-center justify-center bg-white/10 backdrop-blur-sm p-1.5 sm:p-2 group-hover:bg-white/20 transition-all duration-300 shadow-lg">
                <XIcon size={30} round className="sm:w-[35px] sm:h-[35px]" />
              </div>
              <span className="text-white text-[10px] sm:text-xs font-medium mt-1 sm:mt-1.5 group-hover:text-yellow-300 transition-colors duration-300">
                X
              </span>
            </div>
          </TwitterShareButton>

          <TelegramShareButton url={generateShareContent().url}>
            <div className="flex flex-col items-center group transition-transform duration-300 hover:scale-110">
              <div className="rounded-full flex items-center justify-center bg-white/10 backdrop-blur-sm p-1.5 sm:p-2 group-hover:bg-white/20 transition-all duration-300 shadow-lg">
                <TelegramIcon
                  size={30}
                  round
                  className="sm:w-[35px] sm:h-[35px]"
                />
              </div>
              <span className="text-white text-[10px] sm:text-xs font-medium mt-1 sm:mt-1.5 group-hover:text-yellow-300 transition-colors duration-300">
                Telegram
              </span>
            </div>
          </TelegramShareButton>

          <EmailShareButton
            subject={generateShareContent().text}
            body={generateShareContent().title}
            url={generateShareContent().url}
          >
            <div className="flex flex-col items-center group transition-transform duration-300 hover:scale-110">
              <div className="rounded-full flex items-center justify-center bg-white/10 backdrop-blur-sm p-1.5 sm:p-2 group-hover:bg-white/20 transition-all duration-300 shadow-lg">
                <EmailIcon
                  size={30}
                  round
                  className="sm:w-[35px] sm:h-[35px]"
                />
              </div>
              <span className="text-white text-[10px] sm:text-xs font-medium mt-1 sm:mt-1.5 group-hover:text-yellow-300 transition-colors duration-300">
                Email
              </span>
            </div>
          </EmailShareButton>
        </div>
      </div>
    </div>
  );
};
